import * as THREE from "three";
import chaptersData from "../content/chapters.json";
import { clamp, lerp, lerp3, smoothstep } from "../systems/math";

export type Act = (typeof chaptersData.acts)[number];

export type ChapterState = {
  progress: number;
  act: Act;
  actIndex: number;
  localT: number;
  line: string;
  label: string;
};

function findAct(progress: number): { act: Act; index: number; localT: number } {
  const acts = chaptersData.acts;
  const p = clamp(progress, 0, 1);

  for (let i = 0; i < acts.length; i += 1) {
    const act = acts[i];
    const [a, b] = act.range;
    if (p <= b || i === acts.length - 1) {
      const localT = smoothstep(a, b, p);
      return { act, index: i, localT };
    }
  }

  const last = acts[acts.length - 1];
  return { act: last, index: acts.length - 1, localT: 1 };
}

export class ChapterDirector {
  readonly acts = chaptersData.acts;

  sample(progress: number): ChapterState {
    const { act, index, localT } = findAct(progress);
    return {
      progress,
      act,
      actIndex: index,
      localT,
      line: act.line,
      label: act.label,
    };
  }

  applyCamera(
    camera: THREE.PerspectiveCamera,
    progress: number,
    reducedMotion: boolean,
  ) {
    const acts = this.acts;
    const p = clamp(progress, 0, 1);

    // Find surrounding key poses for continuous path
    let i = 0;
    while (i < acts.length - 1 && p > acts[i].range[1]) i += 1;
    const curr = acts[i];
    const next = acts[Math.min(i + 1, acts.length - 1)];
    // Blend within current act toward next act start
    const t = smoothstep(curr.range[0], curr.range[1], p);

    const currPos = curr.camera.position as [number, number, number];
    const nextPos = next.camera.position as [number, number, number];
    const currLook = curr.camera.lookAt as [number, number, number];
    const nextLook = next.camera.lookAt as [number, number, number];

    const pos = lerp3(currPos, nextPos, t * 0.85);
    const look = lerp3(currLook, nextLook, t * 0.85);
    const fov = lerp(curr.camera.fov, next.camera.fov, t * 0.85);

    if (reducedMotion) {
      camera.position.set(currPos[0], currPos[1], currPos[2]);
      camera.lookAt(currLook[0], currLook[1], currLook[2]);
      camera.fov = curr.camera.fov;
      camera.updateProjectionMatrix();
      return;
    }

    camera.position.set(pos[0], pos[1], pos[2]);
    camera.lookAt(look[0], look[1], look[2]);
    camera.fov = fov;
    camera.updateProjectionMatrix();
  }

  applyLights(
    key: THREE.DirectionalLight,
    rim: THREE.DirectionalLight,
    progress: number,
  ) {
    const { act, localT } = findAct(progress);
    const next = this.acts[Math.min(findAct(progress).index + 1, this.acts.length - 1)];
    key.intensity = lerp(act.light.key, next.light.key, localT * 0.5) * 1.4;
    rim.intensity = lerp(act.light.rim, next.light.rim, localT * 0.5) * 1.2;
    rim.color.set(act.id === "inside" || act.id === "threshold" ? "#3ecf7a" : "#8fd4a0");
  }
}
